import React, { ReactElement, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';

import { Spinner } from './index';
import theme from '../styles/theme';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PDFWrapper = styled.div<any>`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    position: relative;

    .react-pdf__Document {
        height: auto;
        width: 100%;
        max-height: 100%;
        position: relative;

        .react-pdf__message,
        .react-pdf {
            height: 100%;
            width: 100%;
            overflow: auto;

            .react-pdf__Page__canvas {
                width: 100%;
            }
        }

        .react-pdf__message--error {
            color: ${theme.constantStyles.colorStyles.white};
        }
    }

    @media only screen and (max-width: 768px) {
        width: 100%;
        min-height: unset;

        .react-pdf__Document {
            width: 100% !important;

            .react-pdf__Page {
                position: relative;
                overflow: hidden !important;
                width: 100% !important;
                justify-content: center;
                display: flex;

                canvas {
                    width: 120% !important;
                    height: auto !important;
                }
            }
        }
    }
`;

const PagesNavigation = styled.div<any>`
    position: absolute;
    bottom: ${(props) => (props.inModal ? '2%' : '3%')};
    left: 50%;
    background-color: ${theme.constantStyles.colorStyles.white};
    transform: translateX(-50%);
    box-shadow: 0 10px 20px 0 rgb(16 36 94 / 20%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media only screen and (max-width: 768px) {
        bottom: 1%;
    }
`;

const PagesNavigationText = styled.span`
    margin: 0 10px;
    font-size: 18px;
`;

const NavigationButton = styled.button`
    background-color: ${theme.constantStyles.colorStyles.white};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    outline: none;
    border-radius: 4px;
    font-size: 18px;
    cursor: pointer;

    &:hover {
        background-color: ${theme.colors.light.grayscale['400']};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media only screen and (max-width: 768px) {
        height: auto;
        width: 44px !important;
    }
`;

const options = {
    cMapUrl: 'cmaps/',
    cMapPacked: true,
};

interface IProps {
    data: any;
    inModal?: boolean;
}

const PDFViewer = ({ data, inModal }: IProps): ReactElement => {
    const [pagesNumber, setPagesNumber] = useState(0);
    const [page, setPage] = useState(1);
    const file = useMemo(() => ({ data }), [data]);

    const onDocumentLoadSuccess = ({ numPages: nextNumPages }: any) => {
        setPagesNumber(nextNumPages);
    };

    const onPreviousClick = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const onNextClick = () => {
        if (page < pagesNumber) {
            setPage(page + 1);
        }
    };

    useEffect(() => {
        if (pagesNumber) {
            for (let i = 0; i < pagesNumber; i++) {
                onNextClick();
            }
        }
        //eslint-disable-next-line
    }, [pagesNumber]);

    return (
        <PDFWrapper inModal={inModal}>
            <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                options={options}
                noData={<Spinner />}
                onLoadError={console.error}
            >
                <Page pageNumber={page} renderTextLayer={false} />
            </Document>
            <PagesNavigation inModal={inModal}>
                <NavigationButton onClick={onPreviousClick} disabled={page === 1}>
                    ‹
                </NavigationButton>
                <PagesNavigationText>
                    {page} of {pagesNumber}
                </PagesNavigationText>
                <NavigationButton onClick={onNextClick} disabled={page === pagesNumber}>
                    ›
                </NavigationButton>
            </PagesNavigation>
        </PDFWrapper>
    );
};

export default PDFViewer;
